import { useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, CreditCard, Truck, Sparkles } from "lucide-react";

/* Dedicated "Order Placed" page — the cart navigates here (via
   navigate("/order-success", { state: { method } })) right after an
   order is successfully placed (online or COD), instead of showing
   a modal on top of the cart page. */
export default function OrderSuccess() {
  const navigate = useNavigate();
  const location = useLocation();

  const method = location.state?.method === "cod" ? "cod" : "online";

  // If someone lands here directly (refresh / no state), send them
  // back to their orders instead of showing a confusing blank page.
  useEffect(() => {
    if (!location.state) {
      navigate("/dashboard/purchase-history", { replace: true });
    }
  }, [location.state, navigate]);

  if (!location.state) return null;

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#fff7fb] via-white to-[#f5f7ff] px-4 py-16 mt-10 md:mt-0 flex items-center justify-center">
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", damping: 22, stiffness: 300 }}
        className="relative bg-white w-full max-w-sm rounded-3xl p-7 text-center shadow-2xl overflow-hidden"
      >
        <div className="absolute -top-16 -right-16 w-40 h-40 bg-pink-100 rounded-full blur-2xl opacity-70" />
        <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-yellow-50 rounded-full blur-2xl opacity-70" />

        <div className="relative">
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 260, damping: 15 }}
            className="mx-auto mb-5 w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center shadow-lg shadow-green-200"
          >
            <CheckCircle2 size={42} className="text-white" strokeWidth={2.4} />
          </motion.div>

          <div className="flex items-center justify-center gap-1.5">
            <Sparkles size={16} className="text-pink-400" />
            <h1 className="font-playfair text-2xl font-bold text-[#1A237E]">
              Order Placed!
            </h1>
            <Sparkles size={16} className="text-pink-400" />
          </div>

          <p className="text-sm text-gray-500 mt-2">
            Your order was placed successfully with{" "}
            <span className="font-semibold text-pink-600">Confidays</span>.
          </p>

          <div className="mt-5 bg-pink-50 border border-pink-100 rounded-2xl p-4 text-left flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Payment Method</p>
              <p className="text-sm font-semibold text-gray-800 mt-0.5">
                {method === "online" ? "Paid Online" : "Cash on Delivery"}
              </p>
            </div>
            <div className="w-9 h-9 rounded-full bg-white border border-pink-100 flex items-center justify-center">
              {method === "online" ? (
                <CreditCard size={16} className="text-pink-500" />
              ) : (
                <Truck size={16} className="text-pink-500" />
              )}
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-4">
            We'll notify you once your order is on its way.
          </p>

          <div className="flex flex-col gap-2 mt-6">
            <Link
              to="/dashboard/purchase-history"
              className="w-full bg-pink-500 hover:bg-pink-600 text-white py-3 rounded-full font-bold text-sm transition"
            >
              View My Orders
            </Link>
            <Link
              to="/products"
              className="w-full border border-gray-200 text-gray-600 py-3 rounded-full font-semibold text-sm hover:bg-gray-50 transition block"
            >
              Continue Shopping
            </Link>
          </div>
        </div>
      </motion.div>
    </main>
  );
}